import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banner,
  EmptyState,
  SearchInput,
  Segmented,
  Spinner,
  StatGrid,
  StatTile,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { nextApi, type AutomationDto, type AutomationTemplateDto } from "../../api/nextApi";
import { EVENTS, notifyAutomationsChanged, onEvent } from "../events";
import { ScreenHeader } from "../page/ScreenHeader";
import { useQuickCreate } from "../quickCreate";
import { useShell } from "../shell/ShellContext";
import { AutomationCard, TemplateCard, useAutomationActions } from "./AutomationParts";
import { AutomationEditor, type AutomationDraft } from "./AutomationEditor";
import { AutomationAiChat, useAutomationAiAvailable } from "./AutomationAiChat";
import { automationToDraft, templateToDraft } from "./AutomationsPanel";
import { loadCatalog, useAutomationsState } from "./automationsStore";
import { matchesQuery } from "../related/resolveSetting";
import "./automations.css";

/** /automations — every automation of the organization, grouped by screen, + the ready-made gallery. */
export function AutomationsPage() {
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("automations.manage");
  const snap = useAutomationsState(true);
  const catalog = snap.catalog;
  const [items, setItems] = useState<AutomationDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"mine" | "gallery" | "ai">("mine");
  const aiAvailable = useAutomationAiAvailable();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<AutomationDraft | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    nextApi.automations
      .list()
      .then(setItems)
      .catch((e: unknown) => setError((e as { message?: string })?.message || "לא הצלחנו לטעון את האוטומציות"));
  }, []);
  useEffect(() => {
    void loadCatalog();
    reload();
  }, [reload]);
  useEffect(() => onEvent(EVENTS.automationsChanged, reload), [reload]);

  const actions = useAutomationActions(reload);
  useQuickCreate("automation", canManage ? () => setDraft({ screenKey: "general" }) : null);

  const screenLabel = (key: string) => catalog?.screens.find((s) => s.key === key)?.label ?? key;
  const shown = useMemo(
    () =>
      (items ?? []).filter(
        (a) => (status === "all" || a.status === status) && (!q.trim() || matchesQuery(`${a.name} ${a.summary} ${a.description ?? ""}`, q)),
      ),
    [items, status, q],
  );
  const groups = useMemo(() => {
    const order = (catalog?.screens ?? []).map((s) => s.key);
    const map = new Map<string, AutomationDto[]>();
    shown.forEach((a) => {
      if (!map.has(a.screenKey)) map.set(a.screenKey, []);
      map.get(a.screenKey)?.push(a);
    });
    return Array.from(map.entries()).sort((x, y) => order.indexOf(x[0]) - order.indexOf(y[0]));
  }, [shown, catalog]);

  const used = useMemo(() => new Set((items ?? []).map((a) => a.templateKey).filter(Boolean)), [items]);
  const gallery = useMemo(() => {
    const map = new Map<string, AutomationTemplateDto[]>();
    (catalog?.templates ?? [])
      .filter((t) => !used.has(t.key) && (!q.trim() || matchesQuery(`${t.name} ${t.description}`, q)))
      .forEach((t) => {
        if (!map.has(t.screen)) map.set(t.screen, []);
        map.get(t.screen)?.push(t);
      });
    return Array.from(map.entries());
  }, [catalog, used, q]);

  const count = (s: string) => (items ?? []).filter((a) => a.status === s).length;

  const addTemplate = async (t: AutomationTemplateDto) => {
    setAdding(t.key);
    try {
      await nextApi.automations.fromTemplate(t.key);
      toast.success(`„${t.name}” הופעלה`);
      notifyAutomationsChanged();
      reload();
    } catch (e) {
      toast.error((e as { message?: string })?.message || "ההפעלה נכשלה");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="atm-page">
      <ScreenHeader
        sub="מיילים, משימות ודוחות שקורים לבד — לפי לוח זמנים או כשמשהו קורה"
        primary={canManage ? { label: "אוטומציה חדשה", icon: "plus", onClick: () => setDraft({ screenKey: "general" }) } : undefined}
        more={[{ key: "refresh", label: "רענון", icon: "refresh", onClick: reload }]}
      />

      <StatGrid min={160}>
        <StatTile label="פעילות" value={items ? count("active") : undefined} icon="check" tone="success" loading={!items} onClick={() => setStatus(status === "active" ? "all" : "active")} active={status === "active"} />
        <StatTile label="מושהות" value={items ? count("paused") : undefined} icon="pause" tone="neutral" loading={!items} onClick={() => setStatus(status === "paused" ? "all" : "paused")} active={status === "paused"} />
        <StatTile label="נכשלו" value={items ? count("error") : undefined} icon="alert" tone="danger" warn={count("error") > 0} loading={!items} onClick={() => setStatus(status === "error" ? "all" : "error")} active={status === "error"} />
        <StatTile label="מוכנות להפעלה" value={catalog ? (catalog.templates.length - used.size) : undefined} icon="sparkle" tone="violet" loading={!catalog} onClick={() => setTab("gallery")} active={tab === "gallery"} />
      </StatGrid>

      <div className="atm-page__filters">
        <Segmented
          ariaLabel="תצוגה"
          value={tab}
          onChange={(v) => setTab(v as "mine")}
          items={[
            { value: "mine", label: "האוטומציות שלנו", count: items?.length },
            { value: "gallery", label: "גלריית תבניות" },
            ...(aiAvailable && canManage ? [{ value: "ai", label: "יצירה עם AI" }] : []),
          ]}
        />
        <SearchInput value={q} onChange={setQ} placeholder="חיפוש אוטומציה…" />
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {!items || !catalog ? (
        <div className="atm-busy">
          <Spinner size={26} />
        </div>
      ) : tab === "ai" ? (
        <div className="atm-page__ai">
          <AutomationAiChat
            screenKey="general"
            catalog={catalog}
            onEdit={(d) => setDraft(d)}
            onOpenAutomation={(id) => {
              const a = items.find((x) => x.id === id);
              if (a) setDraft(automationToDraft(a));
            }}
            onCreated={reload}
          />
        </div>
      ) : tab === "mine" ? (
        groups.length ? (
          groups.map(([key, list]) => (
            <section key={key} className="atm-group">
              <div className="atm-group__head">
                <h3>{screenLabel(key)}</h3>
                <span className="atm-muted">{list.length}</span>
              </div>
              <div className="atm-list">
                {list.map((a) => (
                  <AutomationCard
                    key={a.id}
                    a={a}
                    canManage={canManage}
                    busy={actions.busyId === a.id}
                    onToggle={actions.toggle}
                    onRun={actions.run}
                    onEdit={(x) => setDraft(automationToDraft(x))}
                    onHistory={actions.showHistory}
                    onDelete={actions.askDelete}
                  />
                ))}
              </div>
            </section>
          ))
        ) : (
          <EmptyState icon="sparkle" title={items.length ? "אין אוטומציות שמתאימות לסינון" : "עדיין אין אוטומציות"}>
            {items.length ? "נסו לנקות את הסינון או החיפוש." : "התחילו מתבנית מוכנה בגלריה — או צרו אוטומציה משלכם."}
          </EmptyState>
        )
      ) : gallery.length ? (
        gallery.map(([key, list]) => (
          <section key={key} className="atm-group">
            <div className="atm-group__head">
              <h3>{screenLabel(key)}</h3>
            </div>
            <div className="atm-tpl-grid">
              {list.map((t) => (
                <TemplateCard key={t.key} t={t} canManage={canManage} busy={adding === t.key} onAdd={() => void addTemplate(t)} onCustomize={() => setDraft(templateToDraft(t))} />
              ))}
            </div>
          </section>
        ))
      ) : (
        <EmptyState icon="check" title="כל התבניות כבר פעילות" />
      )}

      <AutomationEditor open={Boolean(draft)} draft={draft} catalog={catalog} onClose={() => setDraft(null)} onSaved={() => reload()} />
      {actions.dialogs}
    </div>
  );
}

export default AutomationsPage;
